import { useApolloClient } from '@apollo/client/react';
import ResponsiveImage from "../../../components/ui/images/ResponsiveImage";
import useDeleteMenuItem from '../../../hooks/MenuItem/useDeleteMenuItem'
import { format } from 'date-fns';
import { useToast } from "../../../context/ToastContext";
import Button from "../../../components/ui/button/Button";
import { useModal } from "../../../hooks/useModal"
import { PencilIcon, TrashBinIcon } from "../../../icons"
import { Modal } from "../../ui/modal";
import MenuItemEdit from "../../../pages/MenuItems/MenuItemEdit";

import type { MenuItem } from "../../../types/menuItemTypes";

type MenuItemCardProps = {
  menuItem: MenuItem;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({
  menuItem,
}) => {
  const client = useApolloClient();
  const toast = useToast();
  const { isOpen, openModal, closeModal } = useModal();
  const { isOpen: isOpenDelete, openModal: openModalDelete, closeModal: closeModalDelete } = useModal();
  const { deleteMenuItem } = useDeleteMenuItem({
    onCompleted: (menuItemDeleted) => {
      if (menuItemDeleted.id) {
        client.refetchQueries({
          include: ["menuItems"], 
        });
        closeModalDelete()
        return toast.success('Menu item delete successful!');
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }
  });

  const updatedAt = new Date(menuItem.updatedAt);
  const image = menuItem.img.medium || '/images/icons/file-image-placeholder.svg'

  return (
    <div
      key={menuItem.id}
      className='flex flex-col justify-between rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]'
    >
      {/* Card Header */}
      <div className="px-6 py-5">
        <ResponsiveImage imageUrl={image} />
        <h3 className="text-2xl mt-2 text-center font-bold text-gray-800 dark:text-white/90">
          {menuItem.mainItemLang.name}
        </h3>
        {menuItem.menuSections && (
          <p className="flex flex-wrap flex-auto gap-2 justify-center mt-1">
            {menuItem.menuSections.map((ms) => (
              <span 
                key={ms.id}
                className="group flex items-center justify-center rounded-full border-[0.7px] border-transparent bg-gray-100 py-1 pl-2.5 pr-2 text-sm text-gray-800 hover:border-gray-200 dark:bg-gray-500 dark:text-white/90 dark:hover:border-gray-800"
              >
                {ms.name}
              </span>
            ))}
          </p>
        )}
        {menuItem.mainItemLang.description && (
          <p className="mt-1 text-md text-left text-gray-400 dark:text-gray-400">
            {menuItem.mainItemLang.description}
          </p>
        )}
      </div>

      {/* Card Body */}
      {/* <div className="p-4 border-t border-gray-200 text-gray-400 dark:border-gray-800 sm:p-6">
        <div className="space-y-6">body</div>
      </div> */}
      <div className="p-4 border-t border-gray-200 text-gray-400 dark:border-gray-800 sm:p-6">
        <div className="flex justify-between">
          <Button 
            size="sm" 
            variant="primary"
            onClick={openModal}
          >
            <PencilIcon width={20} height={20}/>
            Edit
          </Button>
          <div className='text-sm flex self-center text-center mx-1'>
            Last update: {format(updatedAt, 'HH:mm, d MMM yyyy')}
          </div>
          <Button 
            size="sm" 
            variant="delete"
            onClick={openModalDelete}
          >
            <TrashBinIcon width={20} height={20}/>
            Delete
          </Button>
        </div>
      </div>
      {/* Edit Modal */}
      <Modal 
        isOpen={isOpen}
        onClose={closeModal}
        showCloseButton
        className="max-w-[700px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <MenuItemEdit menuItem={menuItem} closeModal={closeModal} />
      </Modal>
      {/* Delete Modal */}
      <Modal 
        isOpen={isOpenDelete}
        onClose={closeModalDelete}
        showCloseButton
        className="max-w-[700px] max-h-[95vh] overflow-x-auto custom-scrollbar">
          <>
            <p className="m-10 text-base text-center font-medium text-gray-800 dark:text-white/90">
              <span>You want to delete menu item?</span>
            </p>
            <div className='mb-10 flex justify-evenly'>
              <Button 
                size="md" 
                variant="primary"
                onClick={() => deleteMenuItem({
                  id: menuItem.id!
                })}
              >
                Yes
              </Button>
              <Button 
                size="md" 
                variant="outline"
                onClick={closeModalDelete}
              >
                Cancel
              </Button>
            </div>
          </>
      </Modal>
    </div>
  );
};

export default MenuItemCard;
